import { Star, Signal, Wifi, BatteryFull } from "lucide-react";
import { PROFILES, IMAGES } from "@/config";

export function PhoneFrame({ children, className = "" }) {
  return (
    <div className={`relative w-[270px] sm:w-[300px] ${className}`} data-testid="phone-mockup">
      <div className="rounded-[46px] border border-ink/25 bg-[#1d1517] p-[9px] shadow-[0_50px_90px_-24px_rgba(78,13,34,0.4)]">
        <div className="relative rounded-[38px] overflow-hidden bg-ivory-light aspect-[9/19]">
          <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-20 h-[18px] bg-[#1d1517] rounded-full z-20" aria-hidden="true" />
          {children}
        </div>
      </div>
    </div>
  );
}

function StatusBar() {
  return (
    <div className="relative z-10 flex justify-between items-center px-7 pt-3.5 text-ink/70">
      <span className="text-[10px] font-sans font-semibold">9:41</span>
      <span className="flex items-center gap-1.5" aria-hidden="true">
        <Signal className="w-3 h-3" />
        <Wifi className="w-3 h-3" />
        <BatteryFull className="w-3.5 h-3.5" />
      </span>
    </div>
  );
}

export function FiveScreen() {
  const p = PROFILES[0];
  return (
    <div className="absolute inset-0 bg-ivory-light flex flex-col">
      <StatusBar />
      <div className="px-5 pt-5 pb-3 flex items-end justify-between">
        <div>
          <p className="text-[9px] uppercase tracking-[0.22em] text-burgundy/70 font-semibold">Bugünün Seçimi</p>
          <h3 className="font-serif text-[22px] leading-tight text-ink">The Five</h3>
        </div>
        <span className="w-8 h-8 rounded-full border border-gold/60 flex items-center justify-center">
          <Star className="w-3.5 h-3.5 fill-gold text-gold" />
        </span>
      </div>
      <div className="px-5 flex-1 flex flex-col">
        <div className="rounded-2xl overflow-hidden relative shadow-lg">
          <img src={p.img} alt={`${p.name} — temsili profil fotoğrafı`} className="w-full h-[235px] object-cover" />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/75 via-ink/25 to-transparent p-4 pt-10">
            <p className="font-serif text-[22px] text-ivory-light leading-none">
              {p.name}, {p.age}
            </p>
            <p className="text-[10px] text-ivory/85 mt-1.5 tracking-wide">
              {p.city}
            </p>
          </div>
        </div>
        <div className="flex justify-center gap-1.5 mt-4" aria-hidden="true">
          {PROFILES.map((_, i) => (
            <span key={i} className={`h-1 rounded-full ${i === 0 ? "w-6 bg-burgundy" : "w-3 bg-burgundy/25"}`} />
          ))}
        </div>
        <div className="mt-4 flex gap-2">
          <span className="flex-1 text-center text-[10px] font-sans font-semibold border border-burgundy/30 text-burgundy rounded-md py-2.5">
            Profili İncele
          </span>
          <span className="flex-1 text-center text-[10px] font-sans font-semibold bg-burgundy text-ivory-light rounded-md py-2.5">
            My Date
          </span>
        </div>
        <p className="mt-auto pb-4 text-center text-[9px] text-ink-muted tracking-wide">Bugün için özenle seçilen 5 profil</p>
      </div>
    </div>
  );
}

export function MyDateScreen() {
  return (
    <div
      className="absolute inset-0 flex flex-col"
      style={{ background: "linear-gradient(180deg, #F6ECEE 0%, #F2DEE4 60%, #EBD0D9 100%)" }}
    >
      <StatusBar />
      <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
        <div className="flex -space-x-4 mb-7">
          <img
            src={PROFILES[0].img}
            alt="Elif — temsili profil"
            className="w-[68px] h-[68px] rounded-full border-2 border-ivory-light object-cover shadow-md"
          />
          <img
            src={PROFILES[1].img}
            alt="Mert — temsili profil"
            className="w-[68px] h-[68px] rounded-full border-2 border-ivory-light object-cover shadow-md"
          />
        </div>
        <p className="text-[9px] uppercase tracking-[0.26em] text-burgundy/70 font-semibold">Karşılıklı İlgi</p>
        <h3 className="font-serif italic text-[34px] text-burgundy leading-tight mt-2">It&rsquo;s a Date</h3>
        <p className="text-[11px] text-ink-soft/80 mt-3 leading-relaxed max-w-[190px]">
          Elif ve Mert için özel eşleşme alanı açıldı.
        </p>
        <span className="mt-7 text-[10px] font-sans font-semibold bg-burgundy text-ivory-light rounded-md px-7 py-3">
          Mesaj Gönder
        </span>
      </div>
      <p className="pb-5 text-center text-[9px] text-ink-muted tracking-wide">My Date</p>
    </div>
  );
}

export function MatchmakerScreen() {
  return (
    <div className="absolute inset-0 bg-ivory-light flex flex-col">
      <StatusBar />
      <div className="px-5 pt-5 pb-3 flex items-center gap-3 border-b border-burgundy/10">
        <span className="relative">
          <img
            src={IMAGES.editorial}
            alt="Çöpçatan profil fotoğrafı"
            className="w-9 h-9 rounded-full object-cover ring-1 ring-gold/60"
          />
          <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#7A9A6D] border border-ivory-light" />
        </span>
        <div>
          <p className="font-serif text-base text-ink leading-none">Çöpçatanın</p>
          <p className="text-[9px] text-ink-muted mt-1 tracking-wide">Sürecine eşlik ediyor</p>
        </div>
      </div>
      <div className="flex-1 px-4 py-4 pb-6 space-y-2.5 overflow-hidden">
        <div className="max-w-[85%] bg-white border border-burgundy/10 rounded-2xl rounded-tl-sm px-3.5 py-2.5 shadow-sm">
          <p className="text-[10.5px] leading-relaxed text-ink-soft">Merhaba, bu turdaki The Five seçimlerini hazırladım.</p>
        </div>
        <div className="max-w-[85%] bg-white border border-burgundy/10 rounded-2xl rounded-tl-sm px-3.5 py-2.5 shadow-sm">
          <p className="text-[10.5px] leading-relaxed text-ink-soft">
            Bu turdaki seçimlerinle ilgili birkaç notumuz var. İstersen birlikte değerlendirebiliriz.
          </p>
          <p className="text-[8px] text-gold mt-1.5 tracking-wide">09:41</p>
        </div>
        <div className="inline-flex bg-white border border-burgundy/10 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
          <span className="flex gap-1" aria-hidden="true">
            <span className="typing-dot w-1.5 h-1.5 rounded-full bg-burgundy/50" />
            <span className="typing-dot w-1.5 h-1.5 rounded-full bg-burgundy/50" />
            <span className="typing-dot w-1.5 h-1.5 rounded-full bg-burgundy/50" />
          </span>
        </div>
      </div>
    </div>
  );
}
